import * as PIXI from "pixi.js";

/**
 * SymbolStripSpinner — a reel strip that fits inside a single symbol.
 *
 * The spin mechanics are lifted from ReelControllerV2 (sprite strip, ticker
 * driven, wrap on overflow, BackOut ease into the landing position) with three
 * differences that make it reusable:
 *
 *   1. GEOMETRY IS PER-INSTANCE. ReelControllerV2 derives SPRITE_COUNT and the
 *      landing distance from Settings.SYMBOLS_VISIBLE at module level, so it is
 *      always the base game's 6-high reel. Here `visibleCount` is a parameter,
 *      and 1 is the interesting case — a strip inside one cell.
 *
 *   2. NO GAME COUPLING. No Redux, no sounds, no blur sheets, no tension, no
 *      coin values, no symbol ids. A symbol-sized strip that dispatches
 *      setSpinComplete would be absurd, and every one of those concerns is why
 *      ReelControllerV2 is 2,600 lines.
 *
 *   3. CONTENT IS THE CALLER'S. The strip owns positions; it does not know what
 *      is drawn in a cell. `createCell` builds an empty cell and `renderCell`
 *      fills it, so a cell can hold a Sprite, a Spine, or a Text. That is what
 *      lets the gamble scatter show skinned level symbols while the bonus
 *      shows something else, from the same class.
 *
 * Cell layout, for visibleCount = N:
 *
 *   count  = N * 2 + 2      1 top buffer + N visible + N landing zone + 1 bottom
 *   visible index = 1 + N   the cell the viewer sees when parked
 *   stop distance = N cells the landing zone travels down into view
 *
 * For N = 1 that is 4 cells, visible at index 2, and a stop that moves 1 cell.
 *
 * Usage:
 *   const strip = new SymbolStripSpinner({ ... });
 *   strip.start();
 *   await strip.stopOn(item);   // resolves when it has settled
 *   strip.destroy();
 */
class SymbolStripSpinner {
  /**
   * @param {Object}        p
   * @param {PIXI.Container} p.parent       Container the strip is added to.
   * @param {number}        p.cellWidth
   * @param {number}        p.cellHeight
   * @param {number}       [p.visibleCount] Cells on screen at rest. Default 1.
   * @param {Array}         p.items         Pool drawn from while spinning.
   * @param {Function} [p.nextItem] Supply the next cell yourself, instead of a
   *   uniform draw from `items`. Omit (null) and the draw is exactly what it
   *   always was — every other caller is unaffected.
   *
   *   Set it when the ORDER matters. A weighted pool controls the ratio over a
   *   long belt and controls nothing about what sits next to what: at a 60/40
   *   split, three of the rarer kind in a row still comes up 6.4% of the time at
   *   every position. A sequence can promise "never more than two together",
   *   which a bag cannot. See makeCardSequence.
   *
   *   Called once per cell, on every wrap and on refill. Returning null falls
   *   back to the uniform draw, so a sequence that runs out cannot blank a cell.
   * @param {Function}      p.createCell    () => PIXI.Container
   * @param {Function}      p.renderCell    (cell, item) => void
   * @param {number}       [p.speedMs]      Milliseconds to travel one cell.
   * @param {number}       [p.accelMs]      Ramp-up to full speed.
   * @param {number}       [p.stopMs]       Landing duration. Omit to derive it
   *                                         from the spin velocity, which is
   *                                         what the base game does.
   * @param {number}       [p.stopSlowdown] Landing speed vs spin speed.
   * @param {number}       [p.overshoot]    BackOut overshoot, 0 = no bounce.
   * @param {number} [p.landingExitSpeed] Cell heights PER MILLISECOND the strip
   *   is still moving at the instant it lands. Omit (null) for the BackOut
   *   landing every caller had before this existed — unchanged, and still the
   *   default.
   *
   *   Set it when a LANDING ANIMATION takes over from the strip. An ease-out
   *   ends at zero velocity by definition, so the cell stops dead and the clip
   *   then starts moving it again from a standstill — which reads as a jerk
   *   however the two are tuned. Given the clip's own opening speed, the strip
   *   decelerates to exactly that instead, and the two motions are continuous.
   *
   *   It also OWNS the landing duration: `stopMs` is ignored, because the
   *   duration is no longer a taste decision. Covering `distance` while going
   *   from the spin's velocity to this one takes 2 x distance / (v0 + v1), and
   *   any other number makes the curve either crawl or lurch to fit.
   */
  constructor({
    parent,
    cellWidth,
    cellHeight,
    visibleCount = 1,
    onRender = null,
    items = [],
    nextItem = null,
    createCell,
    renderCell,
    speedMs = 46,
    accelMs = 209,
    stopMs = null,
    stopSlowdown = 0.693,
    overshoot = 1.1,
    landingExitSpeed = null,
    minStopFraction = 0.75,
    windUpDistance = 0,
    windUpMs = 220,
    targetSpeed = 1,
    decelMs = 0,
    decelDelayMs = 0,
    landingSlowdown = 1,
  }) {
    this.parent = parent;
    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;
    this.visibleCount = visibleCount;
    this.items = items;
    // Reassignable, like `items` — see setPool in GambleCardReel.
    this.nextItem = nextItem;
    // Called after every cell redraw: (cell, item, phase). Optional.
    this.onRender = onRender;
    this.createCell = createCell;
    this.renderCell = renderCell;

    this.speedMs = speedMs;
    this.accelMs = accelMs;
    this.overshoot = overshoot;
    // Cell heights per ms — see the constructor doc. Converted to pixels per ms
    // at stop time and on every resize, because that is the unit the motion runs
    // in and cell height changes.
    this.landingExitSpeed = landingExitSpeed;
    // Pixels per ms at the two ends of the landing. Set in stopOn.
    this._stopEntryPx = 0;
    this._stopExitPx = 0;
    this.minStopFraction = minStopFraction;
    // Wind-up: a short pull BACKWARDS before the spin releases, as a fraction
    // of one cell. PotCollection uses WIND_UP_DISTANCE 0.15. Zero disables it.
    this.windUpDistance = windUpDistance;
    this.windUpMs = windUpMs;

    // ── DECELERATION ──
    //
    // Lifted from ReelControllerV2's tension mode (`spinTension` plus the
    // `_tensionDecelerating` branch of updateSymbolsAbsolute). The strip eases
    // from full speed down to `targetSpeed` over `decelMs`, which is what turns
    // a flat scroll into a wheel of fortune winding down.
    //
    // Defaults are off: targetSpeed 1 and decelMs 0 leave the motion exactly as
    // it was, so the gamble scatter's single-cell strip is unaffected.
    this.targetSpeed = targetSpeed;
    this.decelMs = decelMs;
    // Full speed for this long before the wind-down begins. The reel gets the
    // same shape for free — it spins flat until spinTension() fires — but a
    // strip has no external trigger, so the hold is a duration.
    this.decelDelayMs = decelDelayMs;
    // Extra multiplier on the landing, as TENSION_LANDING_SLOWDOWN is for the
    // reels. Needed because velocity matching against a heavily decelerated
    // spin produces a landing so long it reads as a stall.
    this.landingSlowdown = landingSlowdown;
    this._speedMultiplier = 1;
    this._decelElapsed = 0;
    this.stopSlowdown = stopSlowdown;

    this.cellCount = visibleCount * 2 + 2;
    this.visibleIndex = 1 + visibleCount;
    this.stopCells = visibleCount;

    // Landing duration is VELOCITY MATCHED, not a fixed number. The base game
    // computes it as
    //
    //   (overshoot + 3) x distance / spinVelocity x STOP_SLOWDOWN_FACTOR
    //
    // and since velocity is cellHeight / speedMs, distance / velocity reduces
    // to stopCells x speedMs — so cell size cancels out and the same feel holds
    // at any scale. A hand-picked duration that does not match the speed reads
    // as the strip hanging at the top and then dropping, because the ease has
    // to cover the distance in the wrong amount of time.
    // Null means DERIVE, and when deceleration is in play it must be derived at
    // stop time rather than here — the whole point is that a slower spin earns
    // a longer landing, and the speed is not known until the stop is asked for.
    this._fixedStopMs = stopMs ?? null;
    this.stopMs =
      stopMs ?? (overshoot + 3) * this.stopCells * speedMs * stopSlowdown;

    // Strip lives in its own container so the caller can mask/scale/parent it
    // as one unit without touching the cells.
    this.container = new PIXI.Container();
    this.container.name = "symbol_strip";
    this.cells = [];

    this.isSpinning = false;
    this.isStopping = false;
    this.isWindingUp = false;
    this.destroyed = false;

    this._ticker = null;
    this._elapsed = 0;
    this._lastPosition = 0;
    this._stopElapsed = 0;
    this._stopLastEased = 0;
    this._stopDistance = 0;
    this._stopResolve = null;
    this._landedItem = null;
    // The cell the landing planted its result on — see getLandingCell.
    this._landingCell = null;
    this._windUpLastEased = 0;
    // A cell size that arrived while the strip was moving — see resize.
    this._pendingResize = null;

    this._build();
    if (parent) parent.addChild(this.container);
  }

  // ── Build ────────────────────────────────────────────────────────────────

  _build() {
    for (let i = 0; i < this.cellCount; i++) {
      const cell = this.createCell();
      cell.y = i * this.cellHeight;
      this.cells.push(cell);
      this.container.addChild(cell);
      this._render(cell, this._randomItem());
    }
    // Park so the designated cell sits at y = 0.
    this._offsetAllBy(-this.visibleIndex * this.cellHeight);
    // A strip is taller than its window from the moment it is built, so the
    // cells outside it must not be drawn even before the first spin.
    this._cull();
  }

  /**
   * Draw an item and remember it on the cell.
   *
   * The CURRENT cell size is passed every time rather than captured once. A
   * renderCell that closes over the size keeps scaling to the size the strip
   * was built at, so after any resize the spinning cells and the spine that
   * replaces them no longer agree — which is exactly the mismatch this had.
   */
  _render(cell, item) {
    cell.__item = item;
    this.renderCell(cell, item, this.cellWidth, this.cellHeight);
    // Optional, and the class stays game-free: the caller supplies the hook and
    // owns whatever it does with it. Used to catch a cell whose content changes
    // while the player can see it — see GambleCardReel.
    this.onRender?.(cell, item, this._renderPhase());
  }

  /** Which part of the cycle a render happened in, for onRender. */
  _renderPhase() {
    if (this.isStopping) return "stopping";
    if (this.isWindingUp) return "windup";
    if (this.isSpinning) return "spinning";
    return "at-rest";
  }

  _randomItem() {
    // A caller-supplied sequence wins, and falls through to the uniform draw if
    // it declines — so this can never hand back nothing when `items` has
    // something in it.
    if (typeof this.nextItem === "function") {
      const supplied = this.nextItem();
      if (supplied) return supplied;
    }
    if (!this.items.length) return null;
    return this.items[Math.floor(Math.random() * this.items.length)];
  }

  /**
   * The cell the landing planted its result on.
   *
   * Null until a stop has been requested, and null again on any stop that had
   * no belt to travel — so a caller can tell a real landing from a park.
   */
  getLandingCell() {
    return this._landingCell;
  }

  /** Item currently parked in the visible position. */
  getVisibleItem() {
    return this._landedItem;
  }

  /**
   * The CELL parked in the visible position.
   *
   * Exposed so a caller can put a live spine over the landed cell and hide the
   * flat one, which is the same swap the reels do between ReelControllerV2 and
   * OverlayReelController. Only the landed cell is hidden — on a multi-cell
   * strip the rest stay on screen.
   */
  getVisibleCell() {
    return this._cellAtVisiblePosition();
  }

  /**
   * Draw an item without spinning — the resting state before the first spin,
   * and how the caller seeds a known value.
   */
  setItem(item) {
    // ── NEVER while it is moving ──
    //
    // This draws into the cell AT THE VISIBLE POSITION — the one the player is
    // looking at. At rest that is the whole point: it is how a caller seeds a
    // known value before the first spin.
    //
    // In flight it rewrites the card under the player's eyes. The gamble screen
    // did exactly that: setGambleState fires when the response lands, which is
    // mid-spin, and re-seeded the reel with the new current package — so a card
    // going past turned into the rat the instant a gamble was lost.
    //
    // What lands in the window during a spin is the LANDING's business, and
    // stopOn already owns it: it plants the result on a cell five above the
    // glass and rides it in. There is nothing for this to do until the belt has
    // parked, so it refuses rather than half-doing it — `_landedItem` is not
    // touched either, because a value that was never drawn must not be reported
    // as the one showing.
    if (this.isSpinning || this.isStopping || this.isWindingUp) return;
    this._landedItem = item;
    const cell = this._cellAtVisiblePosition();
    if (cell) this._render(cell, item);
  }

  /**
   * Re-draw every cell from the current pool.
   *
   * Cells are filled once at build time, so a strip constructed before its
   * pool is known stays blank until each cell happens to wrap. That is fine
   * for a one-cell strip, where only the visible cell is ever seen, but a
   * multi-cell reel parks four of its cells on screen and they would all be
   * empty. Call this after changing `items`.
   */
  refill() {
    if (this.destroyed) return;
    // ── NEVER while it is moving ──
    //
    // This redraws EVERY cell in one frame. At rest that is the point: the
    // strip is built before its pool is known, so its cells start blank and
    // something has to draw them.
    //
    // In flight it is a teleport. The gamble screen calls setPool again when
    // the server answers — the ladder it may offer next has changed — and that
    // refilled the belt mid-spin: every card the player was watching swapped
    // for a different one in a single frame, and the rebuilt sequence opens on
    // rats, so a belt of cards became a belt of rats instantly.
    //
    // The pool and the sequence are still swapped by the caller, and that is
    // safe on its own: `items` and `nextItem` are read on each WRAP, so the new
    // belt arrives cell by cell as it moves, which is what evenly-moving means.
    // Only the redraw is refused.
    if (this.isSpinning || this.isStopping || this.isWindingUp) return;
    // ── Filled BOTTOM to TOP, in the order the belt wraps ──
    //
    // `nextItem` may be a SEQUENCE, and a sequence's whole point is what sits
    // beside what — the gamble belt forbids two cards from ever touching. Two
    // things follow, and only the first was obvious.
    //
    // Array order is not screen order: after a spin the cells have wrapped, so
    // filling them by index scatters the runs across the belt.
    //
    // And the direction is not a matter of taste. Cells travel DOWN and wrap
    // from the bottom back to the TOP (see _advance), so the next cell the
    // sequence draws is laid ABOVE the current top one. Filling top-to-bottom
    // ended the sequence on the BOTTOM cell, and the first wrap after that put
    // its next draw beside the cell drawn FIRST — a seam the sequence had never
    // been asked about. Two gold cards touching, on the first wrap after every
    // refill. Vladimir, 2026-09-22: *"i managed to get 2 gold cards
    // consecutively... once i reloaded and started gamble again."*
    //
    // Bottom-to-top closes it: the last cell filled is the top one, so the very
    // next draw continues the run exactly where it left off. It is also the
    // order the player meets the cells in, which is the same statement.
    const inWrapOrder = [...this.cells].sort((a, b) => b.y - a.y);
    for (const cell of inWrapOrder) this._render(cell, this._randomItem());
  }

  _cellAtVisiblePosition() {
    let best = null;
    let bestDist = Infinity;
    for (const cell of this.cells) {
      const d = Math.abs(cell.y);
      if (d < bestDist) {
        bestDist = d;
        best = cell;
      }
    }
    return best;
  }

  // ── Spin ─────────────────────────────────────────────────────────────────

  start() {
    if (this.destroyed || this.isSpinning) return this;
    this.isSpinning = true;
    this.isStopping = false;
    this._elapsed = 0;
    this._lastPosition = 0;
    this._speedMultiplier = 1;
    this._decelElapsed = 0;

    // Wind-up runs first when configured. The strip pulls back against the
    // direction it is about to travel, which reads as the reel loading up
    // before it releases.
    this.isWindingUp = this.windUpDistance > 0;
    this._windUpElapsed = 0;
    this._windUpLastEased = 0;

    this._ticker = () => this._update();
    PIXI.Ticker.shared.add(this._ticker);
    return this;
  }

  /**
   * Ease to a stop with `item` in the visible position.
   * Resolves once the landing has settled.
   */
  stopOn(item) {
    if (this.destroyed) return Promise.resolve();
    if (!this.isSpinning) {
      this.setItem(item);
      return Promise.resolve();
    }
    if (this.isStopping) return this._stopPromise;

    this._landedItem = item;
    this._landingCell = null;
    this.isStopping = true;
    this._stopElapsed = 0;
    this._stopLastEased = 0;

    // ── DRIFT COMPENSATION ──
    //
    // A stop is requested on an arbitrary frame, so the cells are almost never
    // sitting exactly on the grid — they are some fraction of a cell past it.
    // Travelling a whole number of cells from there ends off-grid too, and
    // correcting that afterwards is a visible snap.
    //
    // PotCollectionSymbolController solves it by shortening the journey
    // instead: `adjustedLandingDistance = baseLandingDistance - currentDrift`.
    // The ease then finishes exactly on the grid and nothing has to be
    // corrected. Every cell shares the same sub-cell offset because they all
    // move together, so one cell can measure it for the whole strip.
    const h = this.cellHeight;
    const drift = ((this.cells[0].y % h) + h) % h;
    let distance = this.stopCells * h - drift;

    // Floor the journey so a stop requested just before the grid does not
    // collapse to almost no travel — the result must still visibly drop in.
    const minDistance = h * (this.minStopFraction ?? 0.75);
    if (distance < minDistance) distance += h;
    this._stopDistance = distance;

    // ── LANDING DURATION, VELOCITY MATCHED ──
    const velocity = (h / this.speedMs) * (this._speedMultiplier || 1);

    if (this.landingExitSpeed != null) {
      // ── HANDING OVER TO A CLIP: the duration is arithmetic, not taste ──
      //
      // The landing has to end at the clip's own opening speed (see the
      // constructor doc), so both ends of the deceleration are fixed and the
      // distance is known. A curve going from v0 to v1 over `distance` takes
      //
      //   2 x distance / (v0 + v1)
      //
      // — the average of the two speeds is what covers the ground. Any longer
      // and the curve has to crawl to avoid arriving early; any shorter and it
      // has to lurch. Measured on this strip, the old fixed 560ms was roughly
      // double what its own distance and speeds allow, which is exactly the
      // slow drift-then-stop it produced.
      //
      // Both ends are kept in PIXELS per ms: the curve runs in pixels, and cell
      // height changes on resize.
      this._stopEntryPx = velocity;
      this._stopExitPx = this.landingExitSpeed * h;
      this.stopMs = (2 * distance) / (this._stopEntryPx + this._stopExitPx);
    } else if (this._fixedStopMs == null) {
      // Same formula and same precedence as ReelControllerV2: an explicit
      // duration wins (as TURBO and QUICK set LANDING_DURATION), otherwise
      //
      //   (overshoot + 3) x distance / velocity x STOP_SLOWDOWN x LANDING_SLOWDOWN
      //
      // Computed HERE, not in the constructor, so it sees the decelerated speed
      // and the drift-shortened distance. That is what makes a wound-down spin
      // ease out over seconds instead of snapping in at full-speed timing.
      this.stopMs =
        ((this.overshoot + 3) * distance) /
        velocity *
        this.stopSlowdown *
        this.landingSlowdown;
    }

    // Whichever cell ends up at the visible position must carry the result.
    const target = this._cellArrivingAtVisible(distance);
    if (target) this._render(target, item);
    // Remembered so the caller can see WHICH cell carries the result while it
    // is still far above the window. The gamble belt uses that moment to clear
    // the cells either side of it — inside the glass the same edit would be a
    // redraw the player watches happen.
    this._landingCell = target ?? null;

    this._stopPromise = new Promise((resolve) => {
      this._stopResolve = resolve;
    });
    return this._stopPromise;
  }

  _cellArrivingAtVisible(travel = this.stopCells * this.cellHeight) {
    let best = null;
    let bestDist = Infinity;
    for (const cell of this.cells) {
      // Where this cell ends up, accounting for wrap.
      let end = cell.y + travel;
      const span = this.cellCount * this.cellHeight;
      while (end >= span - this.visibleIndex * this.cellHeight) end -= span;
      const d = Math.abs(end);
      if (d < bestDist) {
        bestDist = d;
        best = cell;
      }
    }
    return best;
  }

  _update() {
    if (this.destroyed) return;

    // ── Frame start: take the new cell size, whatever phase we are in ──
    //
    // PotCollectionSymbolController does it in exactly this place, for exactly
    // this reason: "This ensures resize applies within 1 frame (~16ms)
    // regardless of which animation phase we're in. Previously this only fired
    // at specific safe points which missed controllers in transitional states."
    this._applyPendingResize();

    if (this.isWindingUp) {
      this._updateWindUp();
      return;
    }

    if (this.isStopping) {
      this._updateStop();
      return;
    }

    this._elapsed += PIXI.Ticker.shared.deltaMS;

    // Position tracked at full speed; the ramp scales only what is drawn, so
    // the strip does not "catch up" once acceleration finishes.
    const perMs = this.cellHeight / this.speedMs;
    const expected = this._elapsed * perMs;
    let movement = expected - this._lastPosition;
    this._lastPosition = expected;

    // Deceleration, before acceleration — the order ReelControllerV2 uses.
    //
    // Only the VISUAL movement is scaled; `_lastPosition` above already tracked
    // at full speed. The reel's own comment says why: "This way the gap doesn't
    // grow to compensate — the reel genuinely scrolls slower." Scaling the
    // tracker instead would have the strip race to catch up the moment the
    // deceleration ended.
    if (
      this.decelMs > 0 &&
      this.targetSpeed < 1 &&
      this._elapsed >= this.decelDelayMs
    ) {
      this._decelElapsed += PIXI.Ticker.shared.deltaMS;
      const t = Math.min(this._decelElapsed / this.decelMs, 1);
      const inv = 1 - t;
      const eased = 1 - inv * inv * inv; // ease-out cubic
      this._speedMultiplier = 1 - (1 - this.targetSpeed) * eased;
      movement *= this._speedMultiplier;
    }

    if (this.accelMs > 0) {
      const t = Math.min(this._elapsed / this.accelMs, 1);
      const inv = 1 - t;
      movement *= 1 - inv * inv * inv; // ease-out cubic
    }

    // ── Frame-spike cap ──
    //
    // ReelControllerV2 and PotCollectionSymbolController both carry this, and
    // the reason is written into the Pot: "During resize, React re-renders the
    // entire component tree in the same rAF frame (730ms+). Without a cap, the
    // position calc includes this dead time as movement → sprites overshoot
    // multiple wrap thresholds → infinite spin."
    //
    // A tab that comes back does the same thing, and so does any long frame:
    // the ticker hands over one large step and the strip is asked to travel
    // several cells at once. Capped at 0.95 of a cell, so at most ONE wrap can
    // fall due per frame. It cannot be reached in normal play — a cell takes
    // `speedMs` to pass, and every configured speed is far longer than a frame.
    const maxMovement = this.cellHeight * 0.95;
    if (movement > maxMovement) movement = maxMovement;

    this._advance(movement);
  }

  /** Ease the strip backwards, then hand over to the spin at full travel. */
  _updateWindUp() {
    this._windUpElapsed += PIXI.Ticker.shared.deltaMS;
    const t = Math.min(this._windUpElapsed / this.windUpMs, 1);
    // Ease-out cubic — quick pull, gentle arrival at the held position.
    const eased = 1 - Math.pow(1 - t, 3);
    const target = -this.windUpDistance * this.cellHeight * eased;
    const delta = target - this._windUpLastEased;
    this._windUpLastEased = target;

    if (Math.abs(delta) > 0.0001) this._advance(delta);

    if (t >= 1) {
      this.isWindingUp = false;
      // Reset the clock so acceleration starts from the release, not from
      // when the wind-up began.
      this._elapsed = 0;
      this._lastPosition = 0;
    }
  }

  _updateStop() {
    this._stopElapsed += PIXI.Ticker.shared.deltaMS;
    const t = Math.min(this._stopElapsed / this.stopMs, 1);

    let position;
    if (this.landingExitSpeed != null) {
      // ── CUBIC HERMITE: a curve with a velocity at BOTH ends ──
      //
      // BackOut below cannot do this. Every ease-out arrives at zero speed —
      // that is what the name means — so the cell stops dead and the landing
      // clip then has to start it moving again from nothing. Two motions with a
      // full stop between them, which is the jerk.
      //
      // Hermite is the standard curve through two points with a tangent given at
      // each: here, the spin's velocity going in and the clip's opening velocity
      // coming out. The cell is still travelling when the clip takes over, at
      // the speed the clip is about to continue at, so there is no seam.
      //
      //   p(t) = (t^3 - 2t^2 + t)m0 + (-2t^3 + 3t^2)D + (t^3 - t^2)m1
      //
      // m0 and m1 are the tangents in DISTANCE PER UNIT t, which is why each is
      // its pixels-per-ms multiplied by the duration.
      //
      // Monotonic for every distance this strip can produce — checked from 0.75
      // to 1.75 cells — so it never drifts backwards on the way in.
      const t2 = t * t;
      const t3 = t2 * t;
      const m0 = this._stopEntryPx * this.stopMs;
      const m1 = this._stopExitPx * this.stopMs;
      position =
        (t3 - 2 * t2 + t) * m0 +
        (-2 * t3 + 3 * t2) * this._stopDistance +
        (t3 - t2) * m1;
    } else {
      // BackOut: f(t) = (t-1)^2 * ((s+1)(t-1) + s) + 1
      const s = this.overshoot;
      const t1 = t - 1;
      const eased = t1 * t1 * ((s + 1) * t1 + s) + 1;
      position = this._stopDistance * eased;
    }

    const delta = position - this._stopLastEased;
    this._stopLastEased = position;

    if (Math.abs(delta) > 0.0001) this._advance(delta);

    if (t >= 1) this._finalize();
  }

  /** Move every cell down by `delta`, wrapping and refilling as they pass. */
  /**
   * Stop submitting the cells that cannot be seen.
   *
   * ── The strip is deliberately taller than its window ──
   *
   * `cellCount` is `visibleCount * 2 + 2`, because the landing stages a whole
   * screenful above the glass before it drops it in. So at any instant a good
   * share of the cells are outside the mask: the golden mystery spins FOUR
   * cells to show ONE, and the gamble belt carries twelve to show five.
   *
   * Those cells are still submitted, batched, and then discarded by the mask.
   * On one strip that is nothing. Fifteen Golden Mystery cells spinning at once
   * is fifteen strips, and it stops being nothing.
   *
   * This is `_cullOffscreenSprites` from ReelControllerV2, which the 2026-09-03
   * optimisation pass added there for exactly this reason. The strips never got
   * it.
   *
   * ── Why `renderable` and not `visible` ──
   *
   * Because the OWNERS write `visible`, and the two must not fight. The gamble
   * reel hides the landed cell under a live spine and shows it again when the
   * ride leaves the window — mid-motion, which is when this runs. A cull
   * writing `visible` would put that sprite back under the card it is standing
   * in for. PIXI's `Container.render` returns early on `!visible`, `!renderable`
   * or `worldAlpha <= 0` alike, and skips the children with it, so the two
   * flags compose: the owner says whether the cell is wanted, this says whether
   * it can be seen.
   *
   * ── The band is deliberately generous ──
   *
   * Derived from the strip's own geometry and centred on 0, because that is
   * where `_cellAtVisiblePosition` puts the window. A caller's mask may sit a
   * little off that centre — the gamble screen's is 34 units high of it — so
   * this keeps a whole cell of margin on each side rather than cutting to the
   * measured edge. It culls less; it can never cut something real.
   */
  _cull() {
    const h = this.cellHeight;
    const band = (this.visibleCount * h) / 2 + h;
    for (const cell of this.cells) {
      cell.renderable = Math.abs(cell.y) <= band;
    }
  }

  _advance(delta) {
    if (!delta) return;
    const span = this.cellCount * this.cellHeight;
    // Wrap point is measured from the parked offset, not from zero.
    const limit = span - this.visibleIndex * this.cellHeight;

    for (const cell of this.cells) {
      cell.y += delta;
      // WHILE, not if. The cap above makes one wrap the most that can fall due,
      // and this is the net under it: a single `if` leaves a cell that overshot
      // by more than one span BELOW the strip for good, which empties the window
      // and never fills it again. Pot Collection's own wrap says the same —
      // "use while loop (not if) to handle multiple wraps in case any edge case
      // gets past the movement cap".
      let wrapped = false;
      while (cell.y >= limit) {
        cell.y -= span;
        wrapped = true;
      }
      // Only refill while free-spinning. During the stop the arriving cell
      // already holds the result and must not be overwritten.
      if (wrapped && !this.isStopping) this._render(cell, this._randomItem());
    }

    this._cull();
  }

  _finalize() {
    this.isSpinning = false;
    this.isStopping = false;
    this._removeTicker();

    // The last resize of a drag can arrive after the final frame. Taken here so
    // a parked strip is never left at the size it was spinning at — there is no
    // frame left to apply it in.
    this._applyPendingResize();
    // A parked strip still has cells outside the glass, and it can sit there
    // for the rest of the feature.
    this._cull();

    // NO SNAP HERE ON PURPOSE. The landing distance was shortened by the
    // drift when the stop began, so the ease has already finished on the
    // grid. Correcting positions now is exactly the jump this avoids.

    const resolve = this._stopResolve;
    this._stopResolve = null;
    if (resolve) resolve(this._landedItem);
  }

  _offsetAllBy(dy) {
    for (const cell of this.cells) cell.y += dy;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  /**
   * A new cell size.
   *
   * ── Deferred while the strip is moving ──
   *
   * Ported from PotCollectionSymbolController, which took it from
   * ReelControllerV2: "When spinning, stopping, or winding up, resize is
   * DEFERRED to safe points. This prevents corrupted stop easing distances and
   * sprite position desync that cause the spin to get lost on orientation flip
   * / window resize."
   *
   * The reason it cannot be applied where React calls it: a resize arrives in
   * the middle of a frame, and more than one of them can arrive in the same
   * frame. Every phase of this strip is driven by an accumulated PIXEL distance,
   * so changing the cell height between two of those additions leaves the
   * accumulator in the old scale and the cells in the new one. The next frame
   * then moves by the difference, which is not a movement at all — it is the
   * whole distance travelled so far, re-scaled. That is what emptied the window
   * and never landed.
   *
   * So it is recorded here and applied by _applyPendingResize at the start of a
   * frame, where the whole strip can be converted at once.
   */
  resize(cellWidth, cellHeight) {
    if (this.destroyed) return;
    // ── A ZERO SIZE IS NOT A SIZE ──
    //
    // The layout emits 0x0 while it is still measuring — seen on every
    // orientation change, ahead of the real values (0x0, then 53x53, then
    // 159x159 on one flip).
    //
    // _applyResize converts by RATIO, and a ratio of zero is not a conversion:
    // `cell.y *= 0` collapses every cell in the strip onto the same line, and
    // the accumulated distances go with it. Worse, it cannot be undone — the
    // next real resize divides by the stored height, which is now 0, so the
    // guard there falls back to a ratio of 1 and the strip stays collapsed for
    // the rest of the feature. What the player sees is the whole pool stacked
    // in one window, showing whichever cell happens to be on top.
    //
    // This is the difference from ReelControllerV2, which never has the bug:
    // it recomputes positions ABSOLUTELY from the symbol height, so a bad value
    // is corrected by the next good one. A ratio conversion destroys the
    // information it needs, so it has to refuse bad input instead.
    if (
      !Number.isFinite(cellWidth) ||
      !Number.isFinite(cellHeight) ||
      cellWidth <= 0 ||
      cellHeight <= 0
    ) {
      return;
    }
    if (cellWidth === this.cellWidth && cellHeight === this.cellHeight) {
      this._pendingResize = null;
      return;
    }
    if (this.isSpinning || this.isStopping || this.isWindingUp) {
      // Only the LATEST size matters — a drag emits dozens.
      this._pendingResize = { cellWidth, cellHeight };
      return;
    }
    this._applyResize(cellWidth, cellHeight);
  }

  /** Apply a deferred resize. Called at frame start and when the strip parks. */
  _applyPendingResize() {
    const pending = this._pendingResize;
    if (!pending) return;
    this._pendingResize = null;
    this._applyResize(pending.cellWidth, pending.cellHeight);
    // The band is a multiple of the cell height, so it moved with them.
    this._cull();
  }

  /**
   * Convert the whole strip to a new cell size, mid-flight included.
   *
   * Every field scaled below is a DISTANCE IN PIXELS, and they are only ever
   * read as differences against each other:
   *
   *   _lastPosition     how far the free spin has travelled
   *   _stopDistance     how far the landing has to go, drift already removed
   *   _stopLastEased    how far into that landing the ease has come
   *   _windUpLastEased  how far back the wind-up has pulled
   *
   * So they scale with the cells, by the same ratio, and the motion continues
   * exactly where it was — one frame at the old size, the next at the new one,
   * with no jump between them. The landing still ends on the grid, because the
   * grid scaled too.
   *
   * Nothing here touches TIME. `stopMs` is velocity matched, and the cell size
   * cancels out of that formula (see the constructor), so the same duration is
   * still right at any scale.
   */
  _applyResize(cellWidth, cellHeight) {
    // Refused here too, not only at the entry above: a pending resize recorded
    // before this guard existed, or any future caller, must not be able to
    // reach the ratio with a zero in it. See resize().
    if (
      !Number.isFinite(cellHeight) ||
      cellHeight <= 0 ||
      !Number.isFinite(cellWidth) ||
      cellWidth <= 0
    ) {
      return;
    }
    const ratio = this.cellHeight ? cellHeight / this.cellHeight : 1;
    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;

    if (ratio !== 1) {
      for (const cell of this.cells) cell.y *= ratio;
      this._lastPosition *= ratio;
      this._stopDistance *= ratio;
      this._stopLastEased *= ratio;
      // Pixels per ms, so both ends of a Hermite landing move with cell height
      // exactly as the distance above does. Left alone, a resize mid-landing
      // would hand over at the old cell's speed.
      this._stopEntryPx *= ratio;
      this._stopExitPx *= ratio;
      this._windUpLastEased = (this._windUpLastEased || 0) * ratio;
    }

    // Re-draw at the new size — repositioning alone leaves every cell scaled to
    // the old one.
    for (const cell of this.cells) this._render(cell, cell.__item);
  }

  /**
   * Stop DEAD, wherever it is. No landing, no ease, no chosen item.
   *
   * The reels' answer to a failed request — see ReelControllerV2.freezeSpin and
   * ReelControlContext.freezeReels — brought to the strip. A refused command
   * means the server never told us where to stop, so easing to a stop would
   * mean choosing an outcome ourselves, and even landing on the value the
   * player already held reads as a result rather than as a failure.
   *
   * A stop already in flight is resolved rather than abandoned: its awaiter is
   * the sequence that has to carry on and put the error on screen.
   *
   * Deliberately does NOT touch what is drawn. The cells stay exactly where the
   * last frame left them, mid-travel, which is the whole point — the frozen
   * board is the picture of the moment it failed.
   */
  freeze() {
    if (this.destroyed) return;
    this._removeTicker();
    this.isSpinning = false;
    this.isStopping = false;
    this.isWindingUp = false;
    if (this._stopResolve) {
      const resolve = this._stopResolve;
      this._stopResolve = null;
      resolve(this._landedItem);
    }
  }

  /**
   * Put the belt back on the grid, as if it had never moved.
   *
   * ── Why freezing alone is not enough ──
   *
   * `freeze` stops the ticker WHEREVER it is, which is the point: the picture
   * of a failure should be the frame it failed on. But every cell is then at a
   * fractional offset, so the window straddles a seam and shows two cards where
   * it should show one. A frozen strip is a correct picture and an unusable
   * resting state.
   *
   * `setItem` cannot fix that. It redraws the cell nearest the window — the
   * CONTENT — and has nothing to say about where that cell is sitting.
   *
   * So this is the strip's `restoreReels`: the same rebuild `_build` ends on,
   * cells laid back on whole-cell boundaries and re-parked so the designated
   * one is exactly at y = 0. The caller reseeds what shows immediately after.
   */
  reset() {
    if (this.destroyed) return;
    // Idempotent, and the belt may still be running if this is reached by a
    // path that did not freeze first.
    this.freeze();
    for (let i = 0; i < this.cells.length; i++) {
      this.cells[i].y = i * this.cellHeight;
    }
    this._offsetAllBy(-this.visibleIndex * this.cellHeight);
  }

  _removeTicker() {
    if (this._ticker) {
      PIXI.Ticker.shared.remove(this._ticker);
      this._ticker = null;
    }
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this._removeTicker();
    if (this._stopResolve) {
      this._stopResolve(this._landedItem);
      this._stopResolve = null;
    }
    if (this.container.parent) this.container.parent.removeChild(this.container);
    this.container.destroy({ children: true });
    this.cells = [];
  }
}

export default SymbolStripSpinner;
